// @flow strict

import * as React from 'react';
import Slider from 'react-slick';
import styles from './styles.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PageContentBlock from '../../internal/PageContentBlock';
import ResponsiveImage from '../../internal/ResponsiveImage';
import { Link } from '../../internal/Button';

type CaseStudyType = {|
  companyName: string,
  href: string,
  image: string,
  advisorName: string,
  advisorRole?: string,
  quote: string,
|};

type PropTypes = {|
  caseStudies: Array<CaseStudyType>,
  title: string,
  intro?: string,
  displayQuotes: boolean,
|};

const reactSlickSettings = {
  infinite: true,
  speed: 500,
  autoplaySpeed: 4500,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  centerMode: true,
  arrows: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        centerMode: false,
      },
    },
    {
      breakpoint: 700,
      settings: {
        slidesToShow: 1,
        centerMode: true,
      },
    },
  ],
};

const Card = (props: { ...CaseStudyType, displayQuotes: boolean }) => (
  <div>
    <div className={styles.card}>
      <ResponsiveImage
        className={styles.image}
        src={props.image}
        alt=""
        defaultSize={{ width: 320, height: 200 }}
      />
      <div className={styles.details}>
        <Link className={styles.cardTitle} href={props.href} variant="Underline">
          {props.companyName}
        </Link>
        {props.displayQuotes ? (
          <div className={styles.quote}>{props.quote}</div>
        ) : (
          <>
            <div className={styles.advisorName}>{props.advisorName}</div>
            <div>{props.advisorRole}</div>
          </>
        )}
      </div>
    </div>
  </div>
);

const CaseStudies = (props: PropTypes) => {
  if (props.caseStudies.length === 0) {
    return null;
  }

  if (props.caseStudies.length <= 3) {
    return (
      <PageContentBlock title={props.title} intro={props.intro}>
        <div className={styles.fallbackContainer}>
          {props.caseStudies.map((caseStudy, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card {...caseStudy} key={index} displayQuotes={props.displayQuotes} />
          ))}
        </div>
      </PageContentBlock>
    );
  }

  // react-slick doesn't properly support server-side-rendering with the breakpoints attribute
  // as it sets it's own keys for the children. On the server it renders at desktop size
  // and then on a mobile device, the keys get re-used
  if (typeof window === 'undefined') {
    return (
      <PageContentBlock title={props.title} intro={props.intro}>
        {''}
      </PageContentBlock>
    );
  }

  return (
    <PageContentBlock title={props.title} intro={props.intro}>
      <Slider {...reactSlickSettings}>
        {props.caseStudies.map((caseStudy, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Card {...caseStudy} key={index} displayQuotes={props.displayQuotes} />
        ))}
      </Slider>
    </PageContentBlock>
  );
};

CaseStudies.defaultProps = {
  displayQuotes: false,
};

export default CaseStudies;
